import { useEffect, useState } from "react";
import View from "../../../assets/icons/view.svg";
import Trash from "../../../assets/icons/trash.svg";
import Avatar from "./../../../assets/icons/profilepic.png";
import {
  Pagination,
  Spin,
  message,
  Space,
  Tag,
  Image,
  Checkbox,
  Modal,
  Radio,
} from "antd";
import {
  deleteCustomer,
  getCustomersList,
  exportCustomersToCsv,
} from "../../../api/customer";
import DeleteMemberModal from "../../../components/common/modalMember";
import { useDispatch, useSelector } from "react-redux";
import { resetText } from "../../../store/slices/textSlice";
import Table from "../../../components/common/table";
import { Link } from "react-router-dom";
import {
  AppleOutlined,
  ExportOutlined,
  GoogleOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import Button from "../../../components/common/button";
import colors from "../../../share/constants/constants";
import ExportCustomersModal from "../../../components/common/exportCustomersModal";

const Customers = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPagesize] = useState(10);
  const [deleteId, setDeleteId] = useState();
  const [isDeleted, setIsDeleted] = useState(false);
  const [loader, setLoader] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [render, setRender] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportScope, setExportScope] = useState("selected");

  const text = useSelector((state) => state?.text?.value);
  const dispatch = useDispatch();

  const allCurrentPageSelected = data?.items?.every((item) =>
    selectedRowKeys.includes(item._id)
  );

  const columns = [
    {
      title: (
        <Checkbox
          checked={allCurrentPageSelected}
          indeterminate={
            data?.items?.some((item) => selectedRowKeys.includes(item._id)) &&
            !allCurrentPageSelected
          }
          onChange={(e) => {
            const checked = e.target.checked;
            const currentPageIds = data?.items?.map((item) => item._id) || [];

            if (checked) {
              setSelectedRowKeys((prev) => [
                ...new Set([...prev, ...currentPageIds]),
              ]);
            } else {
              setSelectedRowKeys((prev) =>
                prev.filter((id) => !currentPageIds.includes(id))
              );
            }
          }}
        />
      ),
      key: "selection",
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={selectedRowKeys.includes(record._id)}
          onChange={(e) => {
            const checked = e.target.checked;
            setSelectedRowKeys((prev) =>
              checked
                ? [...prev, record._id]
                : prev.filter((id) => id !== record._id)
            );
          }}
        />
      ),
    },
    {
      title: "Name",
      key: "firstName",
      render: (text, record) => (
        <Space>
          <Image
            src={record?.profileImage || Avatar}
            width={40}
            height={40}
            preview={false}
            alt="Avatar"
            className="rounded object-cover"
          />
          <span>
            {record?.firstName} {record?.lastName}
          </span>
        </Space>
      ),
    },
    {
      title: "Email",
      key: "email",
      dataIndex: "email",
    },
    {
      title: "Phone No.",
      key: "phoneNumber",
      dataIndex: "phoneNumber",
    },
    {
      title: "Earned Points",
      key: "earnedPoints",
      dataIndex: "earnedPoints",
    },
    {
      title: "Sign In Method",
      key: "signInWith",
      render: (text, record) => (
        <Space>
          {record?.signInWith === "GOOGLE" ? (
            <Tag>
              <GoogleOutlined /> Google
            </Tag>
          ) : record?.signInWith === "APPLE" ? (
            <Tag>
              <AppleOutlined /> Apple ID
            </Tag>
          ) : (
            <Tag>
              <UserAddOutlined /> YupUp
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <div className="flex space-x-1">
          <Link to={`/main/customers/${record?._id}`}>
            <div className="w-4 m-auto grid justify-center h-4 pt-1 rounded-full bg-lightLavender cursor-pointer">
              <img src={View} alt="View" />
            </div>
          </Link>
          <div
            onClick={() => showModal(record?._id)}
            className="w-4 m-auto grid justify-center h-4 pt-1 rounded-full bg-lightCrimson cursor-pointer"
          >
            <img src={Trash} alt="Delete" />
          </div>
        </div>
      ),
    },
  ];

  const fetchData = async () => {
    setLoader(true);
    const res = await getCustomersList(currentPage, pageSize, text);
    if (res) {
      setData(res);
    }
    setLoader(false);
  };

  const showExportModal = () => {
    if (selectedRowKeys.length === 0) {
      setExportScope("all");
    }
    setExportModalVisible(true);
  };

  const handleExport = async (exportScope) => {
    setExportLoading(true);
    try {
      const payload = {
        customerIds: exportScope === "selected" ? selectedRowKeys : undefined,
      };

      const response = await exportCustomersToCsv(payload);
      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `customers_export_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(
        `Exported ${
          exportScope === "selected"
            ? selectedRowKeys.length + " selected"
            : "all"
        } customers successfully`
      );
    } catch (error) {
      message.error("Failed to export customers");
      console.error("Export error:", error);
    } finally {
      setExportLoading(false);
      setExportModalVisible(false);
      setSelectedRowKeys([]);
    }
  };

  const showModal = (id) => {
    setIsModalOpen(true);
    setDeleteId(id);
  };

  const hideModal = () => {
    setIsModalOpen(false);
  };

  const handleDelete = async () => {
    setIsModalOpen(false);
    const res = await deleteCustomer(deleteId);
    if (res) {
      message.success("Customer deleted successfully");
      setIsDeleted(!isDeleted);
      setRender(!render);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

   useEffect(() => {
    dispatch(resetText());
    fetchData();
  }, []);

  const handleSizeChange = (current, size) => {
    setPagesize(size);
  };

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [text, pageSize]);

  // Initialization
  useEffect(() => {
    dispatch(resetText());
    fetchData();
  }, []);

  // Data refresh
  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, render, text]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [text, pageSize]);
  
return (
    <div>
      <div className="flex items-center justify-between ml-2 mt-2">
        <Link to={""} className="flex items-center">
          <span className="text-lg font-medium text-black ml-1">Customers</span>
        </Link>
        <div className="mr-2">
          <Button
            icon={<ExportOutlined />}
            title={`Export CSV (${
              selectedRowKeys.length > 0
                ? `${selectedRowKeys.length} Selected`
                : "All Customers"
            })`}
            onClick={showExportModal}
            disabled={false}
            loading={exportLoading}
            color={colors.primaryPurple}
            className="w-24 h-3.8 br"
            borderRadius={5}
          />
        </div>
      </div>

      <ExportCustomersModal
        visible={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        onExport={(scope) => {
          setExportScope(scope);
          handleExport(scope);
        }}
        selectedCount={selectedRowKeys.length}
        exportLoading={exportLoading}
      />

      {/* Main table */}
      <div className="flex items-start justify-start pt-0">
        <Table
          columns={columns}
          data={data?.items}
          loading={loader}
          rowKey="_id"
        />
      </div>

      {/* Pagination */}
      <div className="flex justify-center items-center pagination-container mb-2">
        <Pagination
          current={currentPage}
          total={data?.totalItems}
          pageSize={pageSize}
          onShowSizeChange={handleSizeChange}
          pageSizeOptions={[10, 20, 30, 40]}
          onChange={handlePageChange}
          responsive={true}
          className="custom-pagination"
        />
      </div>

      {/* Delete confirmation modal */}
      {isModalOpen && (
        <DeleteMemberModal
          title="Delete Customer"
          text="Are you sure you want to proceed?"
          onCancel={() => setIsModalOpen(false)}
          onOk={handleDelete}
        />
      )}
    </div>
  );
};

export default Customers;
